import { useState, useEffect } from "react";
import jwtDecode from "jwt-decode";
import { validateUser } from "services/auth";

export type TokenValidationResult = {
  isLoading: boolean;
  isValidated: boolean;
  isTokenExpired: boolean;
  validationStatus: string | null;
  validationMessage: string | null;
  token: string;
  error: string | null;
  decodedToken: any | null;
};

type TokenState = Omit<TokenValidationResult, "token">;

type UseValidateUrlTokenOptions = {
  projectId?: string;
  skip?: boolean;
  search?: string;
  hash?: string;
};

const TOKEN_QUERY_RESOLVE_BASE = "https://invite-token.invalid/";

export function parseTokenFromUrlParts(
  ...parts: Array<string | undefined>
): string {
  for (const raw of parts) {
    const t = parseSingleUrlPart(raw);
    if (t) {
      return t;
    }
  }
  return "";
}

function parseSingleUrlPart(raw: string | undefined): string {
  if (raw === undefined || raw === "") {
    return "";
  }
  let s = raw.trim();
  if (!s) {
    return "";
  }
  if (s.startsWith("#")) {
    s = s.slice(1);
  }
  try {
    const forUrl = s.startsWith("?") || s.startsWith("/") ? s : `?${s}`;
    const resolved = new URL(forUrl, TOKEN_QUERY_RESOLVE_BASE);
    const value = resolved.searchParams.get("token");
    return value ? value.trim() : "";
  } catch {
    // new URL() of a query-string-shaped input against a fixed valid base never throws under test
    /* v8 ignore next */
    return "";
  }
}

function useValidateUrlToken(
  options?: UseValidateUrlTokenOptions
): TokenValidationResult {
  const win =
    // SSR guard: happy-dom always defines window, so the undefined branch is unreachable
    /* v8 ignore next */
    globalThis.window === undefined ? undefined : globalThis.window.location;

  const token = parseTokenFromUrlParts(
    options?.search,
    options?.hash,
    win?.search,
    win?.hash
  );

  const skip = options?.skip ?? false;
  const projectId = options?.projectId;

  const [state, setState] = useState<TokenState>(() => ({
    isLoading: !skip && !!token,
    isValidated: false,
    isTokenExpired: false,
    validationStatus: null,
    validationMessage: null,
    error: null,
    decodedToken: null
  }));

  useEffect(() => {
    if (!token || skip) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: true,
      isValidated: false,
      isTokenExpired: false,
      validationStatus: null,
      validationMessage: null,
      error: null,
      decodedToken: null
    }));

    let cancelled = false;

    const validate = async () => {
      let jwt: any;
      try {
        jwt = jwtDecode(token);
      } catch {
        // teardown race: cleanup cannot run before this synchronous setState resolves
        /* v8 ignore next */
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: "Invalid token."
          }));
        }
        return;
      }

      if (!jwt) {
        // teardown race: cleanup cannot run before this synchronous setState resolves
        /* v8 ignore next */
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: "Invalid token."
          }));
        }
        return;
      }

      const expirationTime = jwt.exp * 1000 - 60000;
      if (Date.now() >= expirationTime) {
        // teardown race: cleanup cannot run before this synchronous setState resolves
        /* v8 ignore next */
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isTokenExpired: true,
            decodedToken: jwt,
            validationMessage:
              "This link has expired. Please request the sender to send you a new invite.",
            error:
              "This link has expired. Please request the sender to send you a new invite."
          }));
        }
        return;
      }

      const result: any = await validateUser(token, projectId);

      if (cancelled) return;

      if (result?.data?.success) {
        setState({
          isLoading: false,
          isValidated: true,
          isTokenExpired: false,
          validationStatus: result.data.success.status ?? null,
          validationMessage: result.data.success.message ?? null,
          error: null,
          decodedToken: jwt
        });
        return;
      }

      const errorMsg =
        result?.response?.data?.error || "User validation failed.";
      setState({
        isLoading: false,
        isValidated: false,
        isTokenExpired: false,
        validationStatus: null,
        validationMessage: null,
        error: errorMsg,
        decodedToken: jwt
      });
    };

    validate();

    return () => {
      cancelled = true;
    };
  }, [token, skip, projectId]);

  return {
    ...state,
    token
  };
}

export default useValidateUrlToken;
